import {trackEvent, TrackEventData} from '@hconnect/common/logging/Analytics'
import {useRequiredUser} from '@hconnect/login'
import {isObject} from 'lodash'
import {useCallback} from 'react'

type DocumentsAnalyticsEvent =
  | {
      name: 'userAddsInstruction'
      data: {instructionId: string}
    }
  | {
      name: 'userEditsInstruction'
      data: {instructionId: string}
    }
  | {
      name: 'userDeletesInstruction'
      data: {instructionId: string}
    }
  | {
      name: 'userOpensInstructionDetails'
      data: {instructionId: string}
    }
  | {
      name: 'userClosesInstructionDetails'
      data: {instructionId: string}
    }

interface CommonAnalyticsData {
  data: {
    plantId?: string
    userId?: string
  }
}

type AnalyticsEvent = DocumentsAnalyticsEvent & CommonAnalyticsData

const trackAnalyticsEvent = <T extends AnalyticsEvent['name']>(
  eventName: T,
  data: TrackEventData
) => trackEvent(eventName, {product: 'cockpit', ...data})

type Params = {
  plantId: string
  country: string
}

export const useTrackDocumentsAnalytics = ({plantId, country}: Params) => {
  const {id: userId} = useRequiredUser()

  return useCallback(
    <T extends AnalyticsEvent['name']>(
      eventName: T,
      data: Extract<AnalyticsEvent, {name: T}> extends {data: infer D} ? D : Record<string, never>
    ) => {
      if (plantId) {
        trackAnalyticsEvent(eventName, {
          timestamp: new Date().toISOString(),
          userId,
          plantId,
          country,
          ...(isObject(data) ? data : {})
        })
      }
    },
    [plantId, userId, country]
  )
}
